import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import PaperCard from '../components/PaperCard';
import Loading from '../components/Loading';
import ErrorMessage from '../components/ErrorMessage';
import { API_V1, apiGet } from '../lib/api';

const API = `${API_V1}/digests`;

function weekKey(w) {
  return `${w.year}-W${String(w.week).padStart(2, '0')}`;
}

// 将周列表按「年月」分组（以周内样本日期归属月份），用于侧边栏折叠展示
function groupWeeksByMonth(weeks) {
  const groups = {};
  for (const w of weeks) {
    const ym = (w.sample_date || '').slice(0, 7); // YYYY-MM
    (groups[ym] = groups[ym] || []).push(w);
  }
  return Object.entries(groups)
    .sort((a, b) => (a[0] < b[0] ? 1 : -1))
    .map(([ym, ws]) => ({ ym, weeks: ws }));
}

export default function WeeklyDigestPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [weeks, setWeeks] = useState([]);
  const [groups, setGroups] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [collapsed, setCollapsed] = useState({});

  const activeWeek = searchParams.get('week') || '';

  useEffect(() => {
    apiGet(`${API}/weeks`)
      .then(d => setWeeks(d.weeks))
      .catch(e => setErr(e.message))
      .finally(() => setLoading(false));
  }, []);

  // 初次进入且无 ?week= 时，默认选中最新一周（replace，不污染历史）
  useEffect(() => {
    if (weeks.length > 0 && !activeWeek) {
      setSearchParams({ week: weekKey(weeks[0]) }, { replace: true });
    }
  }, [weeks, activeWeek, setSearchParams]);

  useEffect(() => {
    if (!activeWeek) return;
    const [y, w] = activeWeek.split('-W');
    setGroups(null);
    apiGet(`${API}/weekly?year=${y}&week=${parseInt(w)}`)
      .then(d => setGroups(d.groups))
      .catch(e => setErr(e.message));
  }, [activeWeek]);

  if (err) return <ErrorMessage message={err} />;
  if (loading) return <Loading />;

  const monthGroups = groupWeeksByMonth(weeks);
  const dateKeys = groups ? Object.keys(groups) : [];
  // 周报 prev/next 顺序：按日期分组顺序拍平（与页面展示顺序一致）
  const flatIds = groups ? dateKeys.flatMap(d => groups[d].map(p => p.id)) : [];
  const totalCount = dateKeys.reduce((n, d) => n + groups[d].length, 0);
  const [wy, ww] = activeWeek ? activeWeek.split('-W') : ['', ''];

  return (
    <div className="page-with-side">
      <aside className="side-list">
        <div className="side-list-header">周报</div>
        {monthGroups.map(g => {
          const isCollapsed = collapsed[g.ym];
          const [y, m] = g.ym.split('-');
          return (
            <div key={g.ym} className="side-group">
              <button className="side-group-title" onClick={() => setCollapsed(p => ({ ...p, [g.ym]: !p[g.ym] }))}>
                <i className={`fas fa-chevron-${isCollapsed ? 'right' : 'down'}`} />
                <span>{y} 年 {parseInt(m)} 月</span>
                <span className="side-group-count">{g.weeks.length}</span>
              </button>
              {!isCollapsed && g.weeks.map(w => (
                <button
                  key={weekKey(w)}
                  className={`side-date${activeWeek === weekKey(w) ? ' active' : ''}`}
                  onClick={() => setSearchParams({ week: weekKey(w) })}
                >
                  第 {w.week} 周
                </button>
              ))}
            </div>
          );
        })}
      </aside>

      <section className="side-content">
        <header className="content-head">
          <h2>{wy} 年第 {parseInt(ww || '0')} 周周报</h2>
          <span className="content-sub">{dateKeys.length} 天 · {totalCount} 篇解读</span>
        </header>
        {!groups || dateKeys.length === 0 ? (
          <div className="empty-hint">该周暂无解读</div>
        ) : (
          <div className="weekly-groups">
            {dateKeys.map(d => (
              <div key={d} className="weekly-group">
                <h3 className="weekly-group-date">{d}</h3>
                <div className="card-grid">
                  {groups[d].map(p => <PaperCard key={p.id} paper={p} navIds={flatIds} />)}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
